import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem, Alert, CircularProgress } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createPackagingRate, updatePackagingRate, type PackagingTaxRate } from '../../api/taxe'
import { errorMessage } from '../../api/client'

const schema = z.object({
  materialCategory: z.enum(['Plastic', 'HartieCarton', 'Sticla', 'Metal', 'Lemn', 'Altele']),
  ratePerKg: z.string().min(1, 'Câmp obligatoriu'),
  currency: z.string().min(1).max(8),
  effectiveFrom: z.string().min(1, 'Câmp obligatoriu'),
  effectiveTo: z.string().optional(),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = { materialCategory: 'Plastic', ratePerKg: '0', currency: 'RON', effectiveFrom: '', effectiveTo: '' }

const CATEGORIES: { value: FormValues['materialCategory']; label: string }[] = [
  { value: 'Plastic', label: 'Plastic' },
  { value: 'HartieCarton', label: 'Hârtie / carton' },
  { value: 'Sticla', label: 'Sticlă' },
  { value: 'Metal', label: 'Metal' },
  { value: 'Lemn', label: 'Lemn' },
  { value: 'Altele', label: 'Altele' },
]

export default function PackagingTaxFormDialog({ rate, onClose }: { rate: PackagingTaxRate | null | 'new'; onClose: () => void }) {
  const open = rate !== null
  const editing = rate !== null && rate !== 'new'
  const qc = useQueryClient()

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema), defaultValues: EMPTY,
  })

  useEffect(() => {
    if (editing) {
      const c = rate as PackagingTaxRate
      reset({ materialCategory: c.materialCategory, ratePerKg: String(c.ratePerKg), currency: c.currency, effectiveFrom: c.effectiveFrom, effectiveTo: c.effectiveTo ?? '' })
    } else if (rate === 'new') {
      reset(EMPTY)
    }
  }, [rate, editing, reset])

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const payload = { ...values, ratePerKg: Number(values.ratePerKg), effectiveTo: values.effectiveTo || null }
      if (editing) return updatePackagingRate((rate as PackagingTaxRate).id, payload)
      return createPackagingRate(payload)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['financiar', 'taxa-ambalaje'] })
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? 'Editare taxă ambalaje' : 'Taxă ambalaje nouă'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Controller name="materialCategory" control={control} render={({ field }) => (
              <TextField {...field} select label="Categorie material" fullWidth error={!!errors.materialCategory} helperText={errors.materialCategory?.message}>
                {CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
              </TextField>
            )} />
            <Stack direction="row" spacing={2}>
              <Controller name="ratePerKg" control={control} render={({ field }) => (
                <TextField {...field} type="number" label="Tarif / kg" fullWidth error={!!errors.ratePerKg} helperText={errors.ratePerKg?.message} />
              )} />
              <Controller name="currency" control={control} render={({ field }) => (
                <TextField {...field} label="Monedă" sx={{ width: 140 }} error={!!errors.currency} helperText={errors.currency?.message} />
              )} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Controller name="effectiveFrom" control={control} render={({ field }) => (
                <TextField {...field} type="date" label="Valabil de la" fullWidth slotProps={{ inputLabel: { shrink: true } }}
                  error={!!errors.effectiveFrom} helperText={errors.effectiveFrom?.message} />
              )} />
              <Controller name="effectiveTo" control={control} render={({ field }) => (
                <TextField {...field} type="date" label="Valabil până la" fullWidth slotProps={{ inputLabel: { shrink: true } }} />
              )} />
            </Stack>
            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Salvează
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
